export const AI_ROLE = {
    USER: 'user',
    ASSISTANT: 'assistant',
} as const;

export type AiRole = (typeof AI_ROLE)[keyof typeof AI_ROLE];

export const AI_MESSAGE_TYPE = {
    TEXT: 'text',
    FILE: 'file',
    SYSTEM: 'system',
    INTERACTIVE: 'interactive',
    INLINE_FORM: 'inline_form',
} as const;

export const INLINE_FORM_KIND = {
    FREE_LINE: 'free_line',
    CREATE_CONTACT: 'create_contact',
    CREATE_WORKSITE: 'create_worksite',
} as const;

export type InlineFormKind = (typeof INLINE_FORM_KIND)[keyof typeof INLINE_FORM_KIND];

export type AiMessageType = (typeof AI_MESSAGE_TYPE)[keyof typeof AI_MESSAGE_TYPE];

export const AI_ASSISTANT_STATUS = {
    IDLE: 'idle',
    RECORDING: 'recording',
    TRANSCRIBING: 'transcribing',
    PROCESSING: 'processing',
    ERROR: 'error',
} as const;

export type AiAssistantStatus = (typeof AI_ASSISTANT_STATUS)[keyof typeof AI_ASSISTANT_STATUS];

export const AI_INTERACTION_TYPE = {
    YES_NO: 'yes_no',
    RADIO: 'radio',
} as const;

export type AiInteractionType = (typeof AI_INTERACTION_TYPE)[keyof typeof AI_INTERACTION_TYPE];

export interface AiFileInfo {
    name: string;
    size: number;
    thumbnail?: string;
}

export interface AiInteractiveOption {
    label: string;
    value: string;
}

export interface FreeLineData {
    title: string;
    price: number;
    quantity: number;
    unit: string;
    vatRate: string;
}

export interface CreateContactData {
    contactType: string;
    firstName: string;
    lastName: string;
    companyName?: string;
    email?: string;
    phone?: string;
}

export interface CreateWorksiteData {
    title: string;
    streetName?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    contact?: string;
}

export interface AiMessage {
    id: string;
    role: AiRole;
    content: string;
    type: AiMessageType;
    documentCreated?: Array<{ type: string; id: string }> | null;
    timestamp: Date;
    fileInfo?: AiFileInfo;
    interactionType?: AiInteractionType;
    options?: AiInteractiveOption[];
    optionSelected?: boolean;
    selectedLabel?: string | null;
    freeLineData?: FreeLineData | null;
    narration?: boolean;
    bubbleIntro?: string | null;
    // Assistant reply produced by the verification auto-fix flow (chip above the bubble).
    autoFix?: { applied: boolean; dismissed?: boolean } | null;
    // For inline form messages — the parent disambig message they belong to + form metadata.
    inlineFormKind?: InlineFormKind;
    inlineFormSearchTerm?: string;
    inlineFormParentId?: string;
}

export const TOOL_LABEL_KEYS: Record<string, string> = {
    search: 'ai.chat.tool.search',
    invoice: 'ai.chat.tool.invoice',
    contact: 'ai.chat.tool.create_contact',
    worksite: 'ai.chat.tool.create_worksite',
    propose_choices: 'ai.chat.tool.prepare_options',
    search_contacts: 'ai.chat.tool.search_contact',
    search_products: 'ai.chat.tool.search_products',
    search_worksites: 'ai.chat.tool.search_worksite',
    search_invoices: 'ai.chat.tool.search_invoices',
    create_draft_invoice: 'ai.chat.tool.create_draft',
    list_vat_rates: 'ai.chat.tool.check_vat',
    list_product_units: 'ai.chat.tool.load_units',
    get_invoice_conditions: 'ai.chat.tool.get_conditions',
    get_invoice: 'ai.chat.tool.load_document',
    create_contact: 'ai.chat.tool.create_contact',
    create_worksite: 'ai.chat.tool.create_worksite',
};

export enum VerificationStepStatus {
    Running = 'running',
    Pass = 'pass',
    Warning = 'warning',
    Issue = 'issue',
    Skipped = 'skipped',
}

export interface VerificationSubStep {
    rule: string;
    status: VerificationStepStatus;
    label: string;
    message?: string | null;
}

export interface VerificationStep {
    step: number;
    total: number;
    label: string;
    status: VerificationStepStatus;
    message?: string;
    rule?: string;
    subSteps?: VerificationSubStep[];
}

export interface VerificationState {
    steps: VerificationStep[];
    isComplete: boolean;
    finalStatus: 'pass' | 'issues_found' | null;
    minimized: boolean;
    autoFixing?: boolean;
}

export type QueuedVerificationEvent =
    | { kind: 'step'; documentId: string; step: VerificationStep }
    | { kind: 'complete'; documentId: string; status: 'pass' | 'issues_found' }
    | { kind: 'autofixing'; documentId: string };

export const MODIFICATION_TOOLS = new Set([
    'invoice',
    'contact',
    'worksite',
    'create_draft_invoice',
    'add_invoice_line',
    'update_invoice_line',
    'add_invoice_section',
    'update_invoice',
    'update_invoice_section',
    'delete_invoice_section',
    'delete_invoice_line',
    'create_contact',
    'create_worksite',
]);
